import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Mic } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { TranscriptDisplay } from '../components/TranscriptDisplay';
import { ControlPanel } from '../components/ControlPanel';
import { SettingsPanel } from '../components/SettingsPanel';
import { StatusIndicator } from '../components/StatusIndicator';
import { BrowserCheck } from '../components/BrowserCheck';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useMicrophone } from '../hooks/useMicrophone';
import { useTranslation } from '../hooks/useTranslation';
import { useTheme } from '../hooks/useTheme';
import { exportAsTxt, exportAsRtf, copyToClipboard } from '../utils/exportUtils';
import { speechLanguageCodes, recognitionLanguages } from '../i18n/translations';

export const HomePage = () => {
  const { t, language, changeLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  
  const {
    isListening,
    isPaused,
    transcript,
    interimTranscript,
    error,
    isSupported,
    lastSaved,
    startListening,
    stopListening,
    pauseListening,
    resumeListening,
    clearTranscript,
    changeLanguage: changeSpeechLang,
  } = useSpeechRecognition();

  const {
    microphones,
    selectedMic,
    selectMicrophone,
    permissionGranted,
  } = useMicrophone();

  // Settings state
  const [recognitionLang, setRecognitionLang] = useState(() => {
    return localStorage.getItem('recognition_language') || 'en';
  });
  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('font_size') || 'large';
  });
  const [lineSpacing, setLineSpacing] = useState(() => {
    return localStorage.getItem('line_spacing') || 'loose';
  });

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('recognition_language', recognitionLang);
  }, [recognitionLang]);

  useEffect(() => {
    localStorage.setItem('font_size', fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('line_spacing', lineSpacing);
  }, [lineSpacing]);

  // Handlers
  const handleStart = useCallback(() => {
    const speechCode = speechLanguageCodes[recognitionLang] || 'en-US';
    startListening(speechCode);
  }, [recognitionLang, startListening]);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(transcript);
    if (success) {
      toast.success(t('toast_copied'));
    } else {
      toast.error(t('toast_error'));
    }
  }, [transcript, t]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      // Spacebar - Start/Pause/Resume
      if (e.code === 'Space' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (!isListening) {
          handleStart();
        } else if (isPaused) {
          resumeListening();
        } else {
          pauseListening();
        }
      }

      // Escape - Stop
      if (e.code === 'Escape') {
        if (isListening) {
          stopListening();
        }
      }

      // Ctrl/Cmd + Shift + C - Copy transcript
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyC') {
        e.preventDefault();
        if (transcript) {
          handleCopy();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isListening, isPaused, handleStart, pauseListening, resumeListening, stopListening, transcript, handleCopy]);

  const handleRecognitionLangChange = useCallback((lang) => {
    setRecognitionLang(lang);
    const speechCode = speechLanguageCodes[lang] || 'en-US';
    changeSpeechLang(speechCode);
  }, [changeSpeechLang]);

  const handleMicChange = useCallback((deviceId) => {
    selectMicrophone(deviceId);
    toast.success(t('toast_mic_changed'));
  }, [selectMicrophone, t]);

  const handleExportTxt = useCallback(() => {
    exportAsTxt(transcript, 'transcript');
    toast.success(t('toast_exported'));
  }, [transcript, t]);

  const handleExportRtf = useCallback(() => {
    exportAsRtf(transcript, 'transcript');
    toast.success(t('toast_exported'));
  }, [transcript, t]);

  const handleClear = useCallback(() => {
    clearTranscript();
  }, [clearTranscript]);

  // Get selected mic label
  const selectedMicLabel = microphones.find(
    (mic) => mic.deviceId === selectedMic
  )?.label;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="home-page">
      <Header
        t={t}
        theme={theme}
        toggleTheme={toggleTheme}
        language={language}
        changeLanguage={changeLanguage}
      />

      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Page Title */}
          <div className="text-center space-y-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              {t('app_title')}
            </h1>
            <p className="text-muted-foreground text-base sm:text-lg">
              {t('app_subtitle')}
            </p>
          </div>

          {/* Browser Compatibility Check */}
          <BrowserCheck t={t} />

          {/* Quick Settings - Language & Microphone */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 p-4 bg-muted/30 rounded-sm border border-border">
            {/* Recognition Language */}
            <div className="flex items-center gap-2">
              <label htmlFor="recognition-lang" className="text-sm font-medium whitespace-nowrap">
                {t('settings_language')}:
              </label>
              <select
                id="recognition-lang"
                value={recognitionLang}
                onChange={(e) => handleRecognitionLangChange(e.target.value)}
                className="h-9 px-3 rounded-sm border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="main-recognition-language"
              >
                {recognitionLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-6 bg-border" />

            {/* Microphone */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <label htmlFor="microphone" className="text-sm font-medium whitespace-nowrap flex items-center gap-1">
                <Mic className="h-4 w-4" />
                {t('settings_microphone')}:
              </label>
              <select
                id="microphone"
                value={selectedMic || ''}
                onChange={(e) => handleMicChange(e.target.value)}
                className="h-9 px-3 rounded-sm border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring flex-1 min-w-0 truncate"
                data-testid="main-microphone-selector"
              >
                {microphones.length === 0 ? (
                  <option value="">No microphone found</option>
                ) : (
                  microphones.map((mic) => (
                    <option key={mic.deviceId} value={mic.deviceId}>
                      {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Status & Settings Row */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <StatusIndicator
                t={t}
                isListening={isListening}
                isPaused={isPaused}
                error={error}
                isSupported={isSupported}
                selectedMicLabel={null}
                permissionGranted={permissionGranted}
              />
            </div>
            <SettingsPanel
              t={t}
              recognitionLang={recognitionLang}
              onRecognitionLangChange={handleRecognitionLangChange}
              microphones={microphones}
              selectedMic={selectedMic}
              onMicChange={handleMicChange}
              fontSize={fontSize}
              onFontSizeChange={setFontSize}
              lineSpacing={lineSpacing}
              onLineSpacingChange={setLineSpacing}
            />
          </div>

          {/* Transcript Display */}
          <TranscriptDisplay
            transcript={transcript}
            interimTranscript={interimTranscript}
            fontSize={fontSize}
            lineSpacing={lineSpacing}
            placeholder={t('transcript_empty')}
            isListening={isListening && !isPaused}
            lastSaved={lastSaved}
            t={t}
          />

          {/* Control Panel */}
          <ControlPanel
            t={t}
            isListening={isListening}
            isPaused={isPaused}
            isSupported={isSupported}
            onStart={handleStart}
            onPause={pauseListening}
            onResume={resumeListening}
            onStop={stopListening}
            onCopy={handleCopy}
            onExportTxt={handleExportTxt}
            onExportRtf={handleExportRtf}
            onClear={handleClear}
            hasTranscript={!!transcript}
          />
        </div>
      </main>

      <Footer t={t} />
    </div>
  );
};
